/**
 * Intercom Widget Utility Functions
 * 
 * These utility functions provide a convenient way to show/hide the Intercom widget
 * programmatically from anywhere in your application.
 * 
 * The widget is hidden by default and only shows when triggered by:
 * - URL parameter ?help-please
 * - Payment failures (past_due or suspended billing status)  
 * - Manual triggers using these utility functions
 */

/**
 * Manually show the Intercom widget
 * @returns {boolean} true if successful, false if function not available
 */
export const showIntercomWidget = () => {
  if (typeof window !== 'undefined' && window.showIntercomWidget) {
    window.showIntercomWidget();
    return true;
  }
  console.warn('⚠️ Intercom widget show function not available');
  return false;
};

/**
 * Manually hide the Intercom widget
 * @returns {boolean} true if successful, false if function not available
 */
export const hideIntercomWidget = () => {
  if (typeof window !== 'undefined' && window.hideIntercomWidget) {
    window.hideIntercomWidget();
    return true;
  }
  console.warn('⚠️ Intercom widget hide function not available');
  return false;
};

/**
 * Check if the Intercom widget is currently visible
 * @returns {boolean} true if visible, false if hidden or function not available
 */
export const isIntercomWidgetVisible = () => {
  if (typeof window !== 'undefined' && window.isIntercomWidgetVisible) {
    return window.isIntercomWidgetVisible();
  }
  console.warn('⚠️ Intercom widget visibility check function not available');
  return false;
};

/**
 * Show Intercom widget for payment-related issues
 * Convenience function that shows the widget and optionally opens a conversation
 * @param {string} message - Optional pre-filled message for payment issues
 */
export const showIntercomForPaymentIssue = (message = "I need help with a payment issue") => {
  if (showIntercomWidget()) {
    // Open Intercom and optionally pre-fill message
    if (window.Intercom) {
      setTimeout(() => {
        window.Intercom('show');
        if (message) {
          window.Intercom('showNewMessage', message);
        }
      }, 500);
    }
  }
};

/**
 * Show Intercom widget for general help
 * Convenience function for general support needs
 * @param {string} message - Optional pre-filled message
 */
export const showIntercomForHelp = (message = "") => {
  if (showIntercomWidget()) {
    if (window.Intercom) {
      setTimeout(() => {
        window.Intercom('show');
        if (message) {
          window.Intercom('showNewMessage', message);
        }
      }, 500);
    }
  }
};

/**
 * Triggers for automatic widget display
 */
export const INTERCOM_TRIGGERS = {
  URL_HELP_PARAM: 'help-please',
  URL_PAYMENT_FAILURE_PARAM: 'payment-failure',
  BILLING_STATUS_PAST_DUE: 'past_due',
  BILLING_STATUS_SUSPENDED: 'suspended'
};
